<template>
  <Dialog
    :open="true"
    @update:open="close"
  >
    <DialogContent class="sm:max-w-6xl">
      <div class="flex flex-col rounded-lg bg-white">
        <!-- Header with close button -->
        <div class="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 class="text-lg font-medium">{{ t('title') }}</h2>
        </div>

        <!-- Preferences Grid View -->
        <div
          v-if="currentView === 'grid'"
          class="flex max-h-[80vh] min-h-[80vh] overflow-hidden"
        >
          <!-- Sidebar with categories -->
          <div
            class="relative w-64 flex-shrink-0 overflow-y-auto border-r border-gray-200 bg-white"
          >
            <!-- Categories list -->
            <div class="space-y-1 p-4 pb-32">
              <!-- All preferences -->
              <button
                class="mb-4 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium
                  transition-colors"
                :class="
                  activeCategory === 'all'
                    ? 'bg-purple-100 text-purple-900'
                    : 'text-gray-700 hover:bg-gray-100'
                "
                @click="setActiveCategory('all')"
              >
                <span>{{ t('all') }}</span>
                <span class="text-xs text-gray-400">{{ specialCategoryCounts.all }}</span>
              </button>

              <!-- Organization preferences -->
              <button
                class="mb-4 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium
                  transition-colors"
                :class="
                  activeCategory === 'shared'
                    ? 'bg-purple-100 text-purple-900'
                    : 'text-gray-700 hover:bg-gray-100'
                "
                @click="setActiveCategory('shared')"
              >
                <span>{{ t('shared') }}</span>
                <span class="text-xs text-gray-400">{{ specialCategoryCounts.shared }}</span>
              </button>

              <!-- User preferences -->
              <button
                class="mb-4 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium
                  transition-colors"
                :class="
                  activeCategory === 'user'
                    ? 'bg-purple-100 text-purple-900'
                    : 'text-gray-700 hover:bg-gray-100'
                "
                @click="setActiveCategory('user')"
              >
                <span>{{ t('user') }}</span>
                <span class="text-xs text-gray-400">{{ specialCategoryCounts.user }}</span>
              </button>

              <!-- Horizontal separator -->
              <div class="my-4 border-t border-gray-200"></div>

              <!-- Active preferences -->
              <button
                class="mb-4 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium
                  transition-colors"
                :class="
                  activeCategory === 'active'
                    ? 'bg-purple-100 text-purple-900'
                    : 'text-gray-700 hover:bg-gray-100'
                "
                @click="setActiveCategory('active')"
              >
                <span>{{ t('active') }}</span>
                <span class="text-xs text-gray-400">{{ specialCategoryCounts.active }}</span>
              </button>

              <!-- Inactive preferences -->
              <button
                class="mb-4 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium
                  transition-colors"
                :class="
                  activeCategory === 'inactive'
                    ? 'bg-purple-100 text-purple-900'
                    : 'text-gray-700 hover:bg-gray-100'
                "
                @click="setActiveCategory('inactive')"
              >
                <span>{{ t('inactive') }}</span>
                <span class="text-xs text-gray-400">{{ specialCategoryCounts.inactive }}</span>
              </button>
            </div>

            <!-- Bottom section with progress bar and new preference button -->
            <div class="absolute bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-4">
              <!-- Progress bar and counter -->
              <div class="mb-3">
                <div class="mb-2 text-sm font-medium text-gray-700">
                  {{ preferencesLeftText }}
                </div>
                <Progress
                  :model-value="progressPercentage"
                  :class="['h-1', preferencesLeft <= 5 ? 'progress-red' : 'progress-purple']"
                />
              </div>

              <!-- New preference button -->
              <button
                class="inline-flex w-full items-center justify-center gap-x-1.5 rounded-md border border-gray-300 bg-white
                  px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline
                  focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500
                  disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="allPreferences.length >= maxTotalPreferences"
                @click="createNewPreference"
              >
                <PlusIcon class="-ml-0.5 h-4 w-4" />
                {{ t('newPreference') }}
              </button>
            </div>
          </div>

          <!-- Preferences area -->
          <div class="flex flex-1 flex-col bg-gray-50">
            <!-- Header with title, search, and close button -->
            <div class="flex items-center justify-between px-4 py-3">
              <h2 class="text-lg font-medium">
                {{
                  activeCategory === 'all'
                    ? t('all')
                    : activeCategory === 'shared'
                      ? t('shared')
                      : activeCategory === 'active'
                        ? t('active')
                        : activeCategory === 'inactive'
                          ? t('inactive')
                          : t('user')
                }}
              </h2>
              <div class="flex items-center space-x-3">
                <div class="relative w-64">
                  <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Search class="h-4 w-4 text-gray-400" />
                  </div>
                  <Input
                    v-model="searchQuery"
                    class="pl-10"
                    :placeholder="t('search')"
                  />
                </div>
              </div>
            </div>

            <!-- Information banner -->
            <div
              v-if="showInfoBanner"
              class="mx-4 mb-4 flex items-center justify-between rounded-lg bg-blue-50 px-4 py-3"
            >
              <div class="flex items-center">
                <Info class="mr-2 h-4 w-4 text-blue-600" />
                <p class="text-sm text-blue-800">
                  {{ t('preferencesInfoBanner') }}
                </p>
              </div>
              <button
                class="text-blue-600 hover:text-blue-800"
                @click="dismissInfoBanner"
              >
                <X class="h-4 w-4" />
                <span class="sr-only">{{ t('dismiss') }}</span>
              </button>
            </div>

            <!-- Preferences grid -->
            <div class="flex-1 overflow-y-auto p-4">
              <div class="grid grid-cols-2 gap-6">
                <div
                  v-for="preference in filteredPreferences"
                  :key="preference.uuid"
                  class="flex cursor-pointer flex-col rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow
                    hover:shadow-md"
                  @click="editPreference(preference)"
                >
                  <!-- Preference header with title, toggle, and actions -->
                  <div class="flex items-start justify-between p-4">
                    <div class="flex min-w-0 flex-1 items-start pr-4">
                      <div class="truncate text-base font-medium text-gray-900">
                        {{ preference.title }}
                      </div>
                    </div>
                    <div class="flex items-center space-x-2">
                      <!-- Toggle switch -->
                      <Switch
                        :checked="Boolean(preference.is_active)"
                        :disabled="
                          isTogglingPreference === preference.uuid ||
                          (!preference.is_active && preferencesLeft <= 0)
                        "
                        class="data-[state=checked]:bg-purple-600 data-[state=unchecked]:bg-gray-200"
                        @update:checked="(value) => togglePreferenceStatus(value, preference)"
                        @click.stop
                      />
                      <!-- More options dropdown -->
                      <DropdownMenu>
                        <DropdownMenuTrigger as-child>
                          <Button
                            variant="secondary"
                            class="flex h-8 w-8 rounded border border-gray-200 p-0 data-[state=open]:bg-muted"
                            @click.stop
                          >
                            <MoreVertical class="h-4 w-4" />
                            <span class="sr-only">{{ t('openMenu') }}</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          class="w-[160px]"
                        >
                          <DropdownMenuItem
                            class="cursor-pointer"
                            @click.stop="editPreference(preference)"
                          >
                            <Pencil class="mr-2 h-4 w-4" />
                            {{ t('edit') }}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            v-if="canDeletePreference(preference)"
                            class="cursor-pointer text-red-600"
                            @click.stop="confirmDeletePreference(preference)"
                          >
                            <Trash class="mr-2 h-4 w-4" />
                            {{ t('delete') }}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            v-else
                            class="cursor-not-allowed text-gray-400"
                            disabled
                          >
                            <Trash class="mr-2 h-4 w-4" />
                            {{ t('delete') }}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  <!-- Preference content -->
                  <div class="flex-1 px-4 pb-4">
                    <div class="line-clamp-3 text-sm leading-relaxed text-gray-600">
                      {{ getContentPreview(preference.content) }}
                    </div>
                  </div>

                  <!-- Preference footer with tags -->
                  <div class="flex items-center justify-between border-t border-gray-100 px-4 py-3">
                    <div class="flex items-center space-x-2">
                      <!-- User tag -->
                      <div
                        class="flex items-center rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3 text-xs font-medium
                          text-gray-800"
                      >
                        <div
                          class="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-medium text-white"
                        >
                          {{ getUserInitials(preference.created_by_user_name) }}
                        </div>
                        <span class="ml-2">{{ preference.created_by_user_name }}</span>
                      </div>
                      <!-- Scope tag -->
                      <div
                        class="flex items-center rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3 text-xs font-medium
                          text-gray-500"
                      >
                        <Lock
                          v-if="preference.scope === 'USER'"
                          class="h-5 w-5 text-xs text-gray-400"
                        />
                        <Users
                          v-else
                          class="h-5 w-5 text-xs text-gray-400"
                        />
                        <span class="ml-2">{{ getScopeLabel(preference.scope) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Show message if no preferences available -->
              <div
                v-if="filteredPreferences.length === 0"
                class="p-4 text-center text-gray-500"
              >
                {{ t('noPreferences') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Preference Form View -->
        <div
          v-if="currentView === 'form'"
          class="max-h-[80vh] min-h-[80vh] overflow-y-auto bg-gray-50 p-6"
        >
          <div class="mx-auto max-w-xl">
            <!-- Back to preferences link -->
            <div class="mb-5">
              <button
                class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                @click="closePreferenceForm"
              >
                <ChevronLeft class="mr-1 h-4 w-4" />
                {{ t('backToAllPreferences') }}
              </button>
            </div>

            <!-- Title header and save button on same axis -->
            <div class="mb-4 flex items-center justify-between">
              <h2 class="text-xl font-medium">
                {{ preferenceForm.title || t('untitledPreference') }}
              </h2>

              <!-- Save button with different states -->
              <Button
                v-if="
                  !preferenceForm.originalPreference ||
                  canEditPreference(preferenceForm.originalPreference)
                "
                variant="default"
                :disabled="isSaving || !isFormValid"
                :class="{
                  'rounded-md px-4 py-2 text-sm font-medium': true,
                  'bg-purple-600 text-white hover:bg-purple-700': !saveSuccess && !saveError,
                  'bg-green-600 text-white': saveSuccess,
                  'bg-red-600 text-white': saveError
                }"
                @click="savePreference"
              >
                <!-- Loading state -->
                <LoaderCircle
                  v-if="isSaving"
                  class="mr-2 h-4 w-4 animate-spin"
                />

                <!-- Success state -->
                <Check
                  v-if="saveSuccess && !isSaving"
                  class="mr-2 h-4 w-4"
                />

                <!-- Error state -->
                <AlertCircle
                  v-if="saveError && !isSaving"
                  class="mr-2 h-4 w-4"
                />

                {{ saveSuccess ? t('saved') : saveError ? t('saveFailed') : t('saveChanges') }}
              </Button>
            </div>

            <!-- Form Content Box -->
            <div class="rounded-lg border border-gray-200 bg-white shadow-sm">
              <!-- Form Fields -->
              <div class="px-6 py-4">
                <!-- Preference Title -->
                <div class="mb-4">
                  <div class="mb-1 flex items-center justify-between">
                    <label
                      for="preferenceTitle"
                      class="block text-sm font-medium"
                    >
                      {{ t('preferenceTitle') }}
                    </label>
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 rounded-md bg-purple-100 px-2 py-1 text-xs font-medium
                        text-purple-700 hover:bg-purple-200 disabled:cursor-not-allowed disabled:opacity-50"
                      :disabled="
                        isSaving ||
                        isGeneratingTitle ||
                        !preferenceForm.content ||
                        (preferenceForm.originalPreference &&
                          !canEditPreference(preferenceForm.originalPreference))
                      "
                      @click="generateTitle"
                    >
                      <span
                        v-if="isGeneratingTitle"
                        class="animate-spin"
                      >
                        <LoaderCircle class="h-3 w-3" />
                      </span>
                      <span v-else>
                        <RotateCw class="h-3 w-3" />
                      </span>
                      {{ isGeneratingTitle ? t('generatingTitle') : t('generateWithAI') }}
                    </button>
                  </div>
                  <InputField
                    v-model="preferenceForm.title"
                    name="preferenceTitle"
                    :disabled="
                      isSaving ||
                      (preferenceForm.originalPreference &&
                        !canEditPreference(preferenceForm.originalPreference))
                    "
                    :placeholder="t('preferenceTitlePlaceholder')"
                  />
                </div>

                <!-- Preference Visibility -->
                <div class="mb-4">
                  <label
                    for="preferenceVisibility"
                    class="mb-1 block text-sm font-medium"
                  >
                    {{ t('preferenceVisibility') }}
                  </label>
                  <SelectField
                    v-model="preferenceForm.scope"
                    name="preferenceVisibility"
                    :disabled="
                      isSaving ||
                      !canChangeScope ||
                      (preferenceForm.originalPreference &&
                        !canEditPreference(preferenceForm.originalPreference))
                    "
                    :placeholder="t('selectVisibility')"
                    :options="[
                      { value: 'USER', title: t('personalPreference') },
                      { value: 'ORGANIZATION', title: t('sharedPreference') }
                    ]"
                  />
                  <p
                    v-if="!canChangeScope"
                    class="mt-1 text-xs text-gray-500"
                  >
                    {{ t('cannotChangeScope') }}
                  </p>
                </div>

                <!-- Preference Content -->
                <div class="mb-3">
                  <label
                    for="preferenceContent"
                    class="mb-1 block text-sm font-medium"
                  >
                    {{ t('preferenceContent') }}
                  </label>
                  <TextAreaField
                    v-model="preferenceForm.content"
                    name="preferenceContent"
                    rows="6"
                    :placeholder="t('preferenceContentPlaceholder')"
                    :disabled="
                      isSaving ||
                      (preferenceForm.originalPreference &&
                        !canEditPreference(preferenceForm.originalPreference))
                    "
                  />
                </div>
              </div>

              <!-- Creator information for read-only view -->
              <div
                v-if="
                  preferenceForm.originalPreference &&
                  !canEditPreference(preferenceForm.originalPreference)
                "
                class="border-t border-gray-100 px-6 pb-4 pt-4"
              >
                <div class="flex items-center">
                  <span class="text-sm text-gray-900">
                    {{ t('createdBy') }}
                  </span>
                  <div
                    class="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-xs font-semibold
                      text-white"
                  >
                    {{ getUserInitials(preferenceForm.originalPreference.created_by_user_name) }}
                  </div>
                  <span class="ml-2 text-sm text-gray-900">
                    {{ preferenceForm.originalPreference.created_by_user_name }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Delete confirmation dialog -->
  <Dialog v-model:open="showDeleteModal">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{{ t('confirmDelete') }}</DialogTitle>
        <DialogDescription>
          {{ t('deletePreferenceQuestion') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          variant="secondary"
          @click="closeDeleteModal"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          variant="destructive"
          @click="deletePreference"
        >
          {{ t('delete') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { Input } from '@/components/ui/input'
  import Progress from '@/components/ui/progress/progress.vue'
  import Switch from '@/components/ui/switch/Switch.vue'
  import { useToast } from '@/components/ui/toast/use-toast'
  import { useTranslation } from '@/composables/useTranslation'
  import type { ChatPreference, CustomerCase } from '@/types/generated'
  import { ChatPreferencesService } from '@/types/generated/services.gen'
  import { format } from 'date-fns'
  import {
    AlertCircle,
    Check,
    ChevronLeft,
    Info,
    LoaderCircle,
    Lock,
    MoreVertical,
    Pencil,
    PlusIcon,
    RotateCw,
    Search,
    Trash,
    Users,
    X
  } from 'lucide-vue-next'
  import { computed, defineEmits, inject, onMounted, ref, type Ref } from 'vue'

  const props = defineProps<{
    source?: string
  }>()

  const emit = defineEmits(['close'])

  // Dialog functionality

  // Router for navigation
  const { t } = useTranslation({
    en: {
      title: 'Chat preferences',
      search: 'Search',
      all: 'All preferences',
      organization: 'Organization',
      user: 'Personal',
      active: 'Active',
      inactive: 'Inactive',
      noPreferences: 'No preferences available',
      edit: 'Edit',
      delete: 'Delete',
      confirmDelete: 'Confirm delete',
      deletePreferenceQuestion: 'Are you sure you want to delete this preference?',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
      preferenceTitle: 'Title',
      preferenceTitlePlaceholder: 'Enter a title for this preference',
      preferenceVisibility: 'Visibility',
      selectVisibility: 'Select who can access this preference...',
      personalPreference: 'Personal preference (only visible to you)',
      sharedPreference: 'Shared preference (visible to everyone in your whole team)',
      preferenceContent: 'Content',
      preferenceContentPlaceholder: 'Enter the content for this preference',
      preferenceScope: 'Visibility',
      selectScope: 'Select visibility...',
      cannotChangeScope: 'You cannot change the scope of an existing preference',
      editPreference: 'Edit Preference',
      backToAllPreferences: 'Back to all preferences',
      saveChanges: 'Save changes',
      saved: 'Saved',
      saveFailed: 'Save failed',
      deleteFailed: 'Delete failed',
      preferenceDeleted: 'Preference deleted',
      untitledPreference: 'Untitled Preference',
      preferenceUpdated: 'Preference Updated',
      preferenceUpdatedDescription: 'Your preference has been successfully updated.',
      preferenceCreated: 'Preference Created',
      preferenceCreatedDescription: 'Your preference has been successfully created.',
      preferenceCreatedInactiveDescription:
        'Your preference has been created but is inactive due to the maximum active preferences limit.',
      errorSavingPreference: 'Error Saving Preference',
      errorSavingPreferenceDescription:
        'There was an error saving your preference. Please try again.',
      generateWithAI: 'Generate with AI',
      generatingTitle: 'Generating...',
      titleGenerationError: 'Error generating title',
      newPreference: 'New Preference',
      case: 'Case',
      openMenu: 'Open menu',
      chatPreferencesTitle: 'Chat Preferences',
      documentGeneratorPreferencesTitle: 'Document Generator Preferences',
      preferencesTitle: 'Preferences',
      preferencesLeft: 'preferences left',
      preferencesUsed: 'preferences used',
      preferencesActive: 'preferences active',
      shared: 'Shared',
      sharedPreferences: 'Shared Preferences',
      preferenceActivated: 'Preference activated',
      preferenceDeactivated: 'Preference deactivated',
      toggleFailed: 'Failed to toggle preference',
      maxActivePreferencesReached: 'Maximum active preferences reached',
      maxActivePreferencesReachedDescription:
        'You have reached the maximum number of active preferences. Please deactivate another preference first.',
      cannotDeleteShared: 'Cannot delete shared preference',
      createdBy: 'Created by',
      preferencesInfoBanner:
        'Preferences allow you to personalize AI responses and are applied to all new chats.',
      dismiss: 'Dismiss',
      close: 'Close'
    },
    de: {
      title: 'Chat-Einstellungen',
      search: 'Suchen',
      all: 'Alle Einstellungen',
      organization: 'Organisation',
      user: 'Persönlich',
      active: 'Aktiv',
      inactive: 'Inaktiv',
      noPreferences: 'Keine Einstellungen verfügbar',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      confirmDelete: 'Löschen bestätigen',
      deletePreferenceQuestion: 'Sind Sie sicher, dass Sie diese Einstellung löschen möchten?',
      cancel: 'Abbrechen',
      save: 'Speichern',
      saving: 'Speichern...',
      preferenceTitle: 'Titel',
      preferenceTitlePlaceholder: 'Geben Sie einen Titel für diese Einstellung ein',
      preferenceVisibility: 'Sichtbarkeit',
      selectVisibility: 'Wählen Sie, wer Zugriff auf diese Einstellung hat...',
      personalPreference: 'Persönliche Einstellung (nur sichtbar für Sie)',
      sharedPreference: 'Geteilte Einstellung (sichtbar für alle in Ihrem Team)',
      preferenceContent: 'Inhalt',
      preferenceContentPlaceholder: 'Geben Sie den Inhalt für diese Einstellung ein',
      preferenceScope: 'Sichtbarkeit',
      selectScope: 'Sichtbarkeit auswählen...',
      cannotChangeScope: 'Sie können die Sichtbarkeit einer bestehenden Einstellung nicht ändern',
      editPreference: 'Einstellung bearbeiten',
      backToAllPreferences: 'Zurück zu allen Einstellungen',
      saveChanges: 'Änderungen speichern',
      saved: 'Gespeichert',
      saveFailed: 'Speichern fehlgeschlagen',
      deleteFailed: 'Löschen fehlgeschlagen',
      preferenceDeleted: 'Einstellung gelöscht',
      untitledPreference: 'Unbenannte Einstellung',
      preferenceUpdated: 'Einstellung aktualisiert',
      preferenceUpdatedDescription: 'Die Einstellung wurde erfolgreich aktualisiert.',
      preferenceCreated: 'Einstellung erstellt',
      preferenceCreatedDescription: 'Die Einstellung wurde erfolgreich erstellt.',
      preferenceCreatedInactiveDescription:
        'Die Einstellung wurde erstellt, ist aber aufgrund des Limits für aktive Einstellungen inaktiv.',
      errorSavingPreference: 'Fehler beim Speichern der Einstellung',
      errorSavingPreferenceDescription:
        'Beim Speichern der Einstellung ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
      generateWithAI: 'Mit KI generieren',
      generatingTitle: 'Generiere...',
      titleGenerationError: 'Fehler beim Generieren des Titels',
      newPreference: 'Neue Einstellung',
      case: 'Fall',
      openMenu: 'Menü öffnen',
      chatPreferencesTitle: 'Chat-Einstellungen',
      documentGeneratorPreferencesTitle: 'Dokumentgenerator-Einstellungen',
      preferencesTitle: 'Einstellungen',
      preferencesLeft: 'Einstellungen übrig',
      preferencesUsed: 'Einstellungen verwendet',
      preferencesActive: 'Einstellungen aktiv',
      shared: 'Geteilt',
      sharedPreferences: 'Geteilte Einstellungen',
      preferenceActivated: 'Einstellung aktiviert',
      preferenceDeactivated: 'Einstellung deaktiviert',
      toggleFailed: 'Einstellung konnte nicht umgeschaltet werden',
      maxActivePreferencesReached: 'Maximum aktive Einstellungen erreicht',
      maxActivePreferencesReachedDescription:
        'Sie haben die maximale Anzahl aktiver Einstellungen erreicht. Bitte deaktivieren Sie zuerst eine andere Einstellung.',
      cannotDeleteShared: 'Geteilte Einstellung kann nicht gelöscht werden',
      createdBy: 'Erstellt von',
      preferencesInfoBanner:
        'Einstellungen ermöglichen es Ihnen, KI-Antworten zu personalisieren und werden auf alle neuen Chats angewendet.',
      dismiss: 'Schließen',
      close: 'Schließen'
    }
  })

  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const { toast } = useToast()

  // Get current user info (you'll need to inject this or get it from your auth system)
  const currentUser = inject<Ref<any>>('currentUser', ref(null))

  // View state (grid or form)
  const currentView = ref('grid')

  // Search functionality
  const searchQuery = ref('')

  // Active category
  const activeCategory = ref('all')

  // Preferences data
  const allPreferences = ref<ChatPreference[]>([])
  const organizationPreferences = ref<ChatPreference[]>([])
  const userPreferences = ref<ChatPreference[]>([])
  const activePreferences = ref<ChatPreference[]>([])
  const inactivePreferences = ref<ChatPreference[]>([])

  // Count for special categories
  const specialCategoryCounts = computed(() => {
    return {
      all: allPreferences.value.length,
      shared: organizationPreferences.value.length,
      user: userPreferences.value.length,
      active: activePreferences.value.length,
      inactive: inactivePreferences.value.length
    }
  })

  // Get max active preferences from backend (fallback to 30 if not available)
  const maxActivePreferences = computed(() => {
    if (allPreferences.value.length > 0 && allPreferences.value[0].max_active_preferences) {
      // Convert string to number since the API returns it as a string
      return parseInt(allPreferences.value[0].max_active_preferences, 10)
    }
    return 30 // Fallback to hardcoded value if backend doesn't provide it
  })

  // Get max total preferences from backend (fallback to 100 if not available)
  const maxTotalPreferences = computed(() => {
    if (allPreferences.value.length > 0 && allPreferences.value[0].max_preferences) {
      // Convert string to number since the API returns it as a string
      return parseInt(allPreferences.value[0].max_preferences, 10)
    }
    return 100 // Fallback to hardcoded value if backend doesn't provide it
  })

  // Progress bar calculations
  const activePreferencesCount = computed(() => activePreferences.value.length)
  const preferencesLeft = computed(() =>
    Math.max(0, maxActivePreferences.value - activePreferencesCount.value)
  )
  // Progress bar shows active preferences (starts at 0% and increases)
  const progressPercentage = computed(
    () => (activePreferencesCount.value / maxActivePreferences.value) * 100
  )
  const preferencesLeftText = computed(() => {
    const active = activePreferencesCount.value
    return `${active}/${maxActivePreferences.value} ${t('preferencesActive')}`
  })

  // Preference form state
  const preferenceForm = ref<{
    uuid: string
    title: string
    content: string
    scope: string
    originalPreference?: ChatPreference
  }>({
    uuid: '',
    title: '',
    content: '',
    scope: 'ORGANIZATION'
  })

  // Loading states
  const isLoading = ref(false)
  const isSaving = ref(false)
  const saveSuccess = ref(false)
  const saveError = ref(false)
  const isDeleting = ref(false)
  const isGeneratingTitle = ref(false)
  const isTogglingPreference = ref<string | null>(null)

  // Info banner state
  const showInfoBanner = ref(true)

  // Delete confirmation
  const showDeleteModal = ref(false)
  const preferenceToDelete = ref<ChatPreference | null>(null)

  // Filtered preferences based on search and category
  const filteredPreferences = computed(() => {
    let preferences: ChatPreference[] = []

    switch (activeCategory.value) {
      case 'shared':
        preferences = organizationPreferences.value
        break
      case 'user':
        preferences = userPreferences.value
        break
      case 'active':
        preferences = activePreferences.value
        break
      case 'inactive':
        preferences = inactivePreferences.value
        break
      default:
        preferences = allPreferences.value
    }

    // Filter by search query
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase()
      preferences = preferences.filter(
        (preference) =>
          preference.title.toLowerCase().includes(query) ||
          preference.content.toLowerCase().includes(query)
      )
    }

    return preferences
  })

  const isFormValid = computed(() => {
    return (
      preferenceForm.value.title.trim().length > 0 && preferenceForm.value.content.trim().length > 0
    )
  })

  const canChangeScope = computed(() => {
    // Only allow changing scope for new preferences
    return !preferenceForm.value.uuid
  })

  // Fetch preferences from API using query parameters
  const fetchPreferences = async () => {
    isLoading.value = true

    try {
      // Build query parameters for the API call
      const params: any = {
        limit: 10000
      }

      // Add source filter if specified
      if (props.source && props.source !== 'ALL') {
        params.source = props.source.toLowerCase()
      }

      // Session type filtering removed - preferences are now unified for both session types

      // Fetch all preferences with source filtering - backend handles the filtering
      const response = await ChatPreferencesService.chatPreferencesList(params)
      const allFetchedPreferences = response.results || []

      // Filter by scope for display in sidebar categories
      organizationPreferences.value = allFetchedPreferences.filter(
        (m: ChatPreference) => m.scope === 'ORGANIZATION'
      )
      userPreferences.value = allFetchedPreferences.filter(
        (m: ChatPreference) => m.scope === 'USER'
      )
      activePreferences.value = allFetchedPreferences.filter(
        (m: ChatPreference) => Boolean(m.is_active) === true
      )
      inactivePreferences.value = allFetchedPreferences.filter(
        (m: ChatPreference) => Boolean(m.is_active) === false
      )
      allPreferences.value = allFetchedPreferences

      console.log('Total preferences:', allPreferences.value.length)
      console.log('Max preferences from backend:', allFetchedPreferences[0]?.max_preferences)
      console.log(
        'Sample preference is_active values:',
        allFetchedPreferences
          .slice(0, 3)
          .map((p) => ({ title: p.title, is_active: p.is_active, type: typeof p.is_active }))
      )
    } catch (error) {
      console.error('Failed to fetch preferences:', error)
    } finally {
      isLoading.value = false
    }
  }

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm')
    } catch (e) {
      return dateString
    }
  }

  // Get content preview
  const getContentPreview = (content: string, maxLength = 150) => {
    if (!content) return ''
    if (content.length <= maxLength) return content
    return content.substring(0, maxLength) + '...'
  }

  // Get scope label
  const getScopeLabel = (scope: string | undefined) => {
    switch (scope) {
      case 'ORGANIZATION':
        return t('shared')
      case 'USER':
        return t('user')
      case 'CASE':
        return t('case')
      default:
        return scope || t('user')
    }
  }

  // Get user initials from name
  const getUserInitials = (name: string) => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Check if user can delete a preference
  const canDeletePreference = (preference: ChatPreference) => {
    // Users can always delete their own preferences
    if (preference.scope === 'USER') {
      return true
    }

    // For shared preferences, only the creator can delete them
    if (preference.scope === 'ORGANIZATION') {
      // If we have current user info, compare with the preference creator
      if (currentUser.value && preference.created_by_user_name) {
        const currentUserName = currentUser.value.first_name + ' ' + currentUser.value.last_name

        return currentUserName === preference.created_by_user_name
      }
      return false
    }

    return false
  }

  // Check if user can edit a preference
  const canEditPreference = (preference: ChatPreference) => {
    // Users can always edit their own preferences
    if (preference.scope === 'USER') {
      return true
    }

    // For shared preferences, only the creator can edit them
    if (preference.scope === 'ORGANIZATION') {
      // If we have current user info, compare with the preference creator
      if (currentUser.value && preference.created_by_user_name) {
        const currentUserName = currentUser.value.first_name + ' ' + currentUser.value.last_name

        return currentUserName === preference.created_by_user_name
      }
      return false
    }

    return false
  }

  // Toggle preference status
  const togglePreferenceStatus = async (newValue: boolean, preference: ChatPreference) => {
    if (isTogglingPreference.value === preference.uuid) return

    // Prevent activating if we're at max active preferences
    if (newValue && preferencesLeft.value <= 0) {
      toast({
        title: t('maxActivePreferencesReached'),
        description: t('maxActivePreferencesReachedDescription'),
        variant: 'destructive'
      })
      return
    }

    isTogglingPreference.value = preference.uuid

    try {
      await ChatPreferencesService.chatPreferencesUpdate({
        uuid: preference.uuid,
        requestBody: {
          title: preference.title,
          content: preference.content,
          scope: preference.scope || 'ORGANIZATION',
          source: props.source || 'AI_CHAT',
          is_active: newValue
        }
      })

      // Update the preference in our local state
      preference.is_active = newValue

      // Refresh the filtered lists
      await fetchPreferences()

      toast({
        title: newValue ? t('preferenceActivated') : t('preferenceDeactivated'),
        variant: 'success'
      })
    } catch (error) {
      console.error('Failed to toggle preference status:', error)
      toast({
        title: t('toggleFailed'),
        variant: 'destructive'
      })
    } finally {
      isTogglingPreference.value = null
    }
  }

  // Set active category
  const setActiveCategory = (category: string) => {
    activeCategory.value = category
  }

  // Dismiss info banner
  const dismissInfoBanner = () => {
    showInfoBanner.value = false
  }

  // Generate title using AI
  const generateTitle = async () => {
    if (!preferenceForm.value.content || isGeneratingTitle.value) return

    isGeneratingTitle.value = true

    // Clear the current title before generating a new one
    preferenceForm.value.title = ''

    try {
      const response = await ChatPreferencesService.chatPreferencesGenerateTitleCreate({
        requestBody: {
          content: preferenceForm.value.content,
          title: '' // Add required title field
        }
      })

      // Simulate streaming effect for better UX
      const title = response.title
      let currentIndex = 0

      // Stream the title character by character
      const streamInterval = setInterval(() => {
        if (currentIndex < title.length) {
          preferenceForm.value.title += title[currentIndex]
          currentIndex++
        } else {
          clearInterval(streamInterval)
          isGeneratingTitle.value = false
        }
      }, 15) // Adjust speed as needed
    } catch (error) {
      console.error('Error generating title:', error)
      toast({
        title: t('titleGenerationError'),
        variant: 'destructive'
      })
      isGeneratingTitle.value = false
    }
  }

  // Edit preference
  const editPreference = (preference: ChatPreference) => {
    preferenceForm.value = {
      uuid: preference.uuid,
      title: preference.title,
      content: preference.content,
      scope: preference.scope || 'ORGANIZATION'
    }

    // Store the preference being edited for access in the form
    preferenceForm.value.originalPreference = preference

    console.log('preferenceForm', preferenceForm.value)
    currentView.value = 'form'
  }

  // Create new preference
  const createNewPreference = () => {
    resetPreferenceForm()
    currentView.value = 'form'
  }

  // Close preference form
  const closePreferenceForm = () => {
    currentView.value = 'grid'
    resetPreferenceForm()
  }

  // Reset preference form
  const resetPreferenceForm = () => {
    preferenceForm.value = {
      uuid: '',
      title: '',
      content: '',
      scope: 'ORGANIZATION',
      originalPreference: undefined
    }
  }

  // Save preference
  const savePreference = async () => {
    if (!isFormValid.value) return

    try {
      // Reset states
      isSaving.value = true
      saveSuccess.value = false
      saveError.value = false

      // For new preferences, check if we're at max active preferences
      // For existing preferences, preserve the current active status
      const shouldBeActive = preferenceForm.value.uuid
        ? undefined // Don't change active status for existing preferences
        : preferencesLeft.value > 0 // Only check limit for new preferences

      const requestBody: any = {
        title: preferenceForm.value.title,
        content: preferenceForm.value.content,
        scope: preferenceForm.value.scope,
        source: props.source || 'AI_CHAT'
      }

      // Only add is_active for new preferences
      if (!preferenceForm.value.uuid) {
        requestBody.is_active = shouldBeActive
      }

      console.log('Saving preference with request body:', requestBody)

      if (preferenceForm.value.uuid) {
        // Update existing preference
        await ChatPreferencesService.chatPreferencesUpdate({
          uuid: preferenceForm.value.uuid,
          requestBody: requestBody
        })
        toast({
          title: t('preferenceUpdated'),
          description: t('preferenceUpdatedDescription')
        })
      } else {
        // Create new preference
        await ChatPreferencesService.chatPreferencesCreate({
          requestBody: requestBody
        })

        if (!shouldBeActive) {
          toast({
            title: t('preferenceCreated'),
            description: t('preferenceCreatedInactiveDescription'),
            variant: 'default'
          })
        } else {
          toast({
            title: t('preferenceCreated'),
            description: t('preferenceCreatedDescription')
          })
        }
      }

      // Show success state
      saveSuccess.value = true

      // Reset success state after 2 seconds
      setTimeout(() => {
        saveSuccess.value = false
        // Refresh preferences list and go back to grid view
        fetchPreferences()
        currentView.value = 'grid'
      }, 1500)
    } catch (error) {
      console.error('Failed to save preference:', error)
      toast({
        title: t('errorSavingPreference'),
        description: t('errorSavingPreferenceDescription'),
        variant: 'destructive'
      })

      // Show error state
      saveError.value = true

      // Reset error state after 2 seconds
      setTimeout(() => {
        saveError.value = false
      }, 2000)
    } finally {
      isSaving.value = false
    }
  }

  // Confirm delete preference
  const confirmDeletePreference = (preference: ChatPreference) => {
    preferenceToDelete.value = preference
    showDeleteModal.value = true
  }

  // Close delete modal
  const closeDeleteModal = () => {
    showDeleteModal.value = false
    preferenceToDelete.value = null
  }

  // Delete preference
  const deletePreference = async () => {
    if (!preferenceToDelete.value) return

    isDeleting.value = true

    try {
      await ChatPreferencesService.chatPreferencesDestroy({
        uuid: preferenceToDelete.value.uuid
      })

      toast({
        title: t('preferenceDeleted'),
        variant: 'success'
      })

      // Refresh preferences
      await fetchPreferences()
      closeDeleteModal()
    } catch (error) {
      console.error('Failed to delete preference:', error)
      toast({
        title: t('deleteFailed'),
        variant: 'destructive'
      })
    } finally {
      isDeleting.value = false
    }
  }

  // Close dialog
  const close = () => {
    emit('close')
  }

  // Fetch preferences on mount
  onMounted(() => {
    fetchPreferences()
  })
</script>

<style scoped>
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .rotate-180 {
    transform: rotate(180deg);
  }

  /* Progress bar color variants */
  :deep(.progress-purple .bg-primary) {
    background-color: rgb(147 51 234); /* purple-600 */
  }

  :deep(.progress-red .bg-primary) {
    background-color: rgb(220 38 38); /* red-600 */
  }
</style>
